import { createContext, PropsWithChildren, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { useArchiveContext } from './ArchiveContext'
import DataContextValue from './DataContextValue'
import { useApiContext } from './ApiContext'
import { useUserContext } from './UserContext'
import { useToastContext } from './ToastContext'
import { useAction } from '../../hooks/action'
import { useImmutableObject } from '../../hooks/immutable'
import { usePaginatedTable } from '../../hooks/pagination'
import { useProvideContext } from '../../hooks/context'
import { mapReadTableResponse, mapReadResponse, throwIfResponseNotOk } from '../../tools/api'
import Device from '../../models/Device'
import TableControl from '../../models/TableControl'

export interface DeviceContextValue extends DataContextValue {
    organizationDevices: TableControl<Device>
    // allDevices: TableControl<Device>
    selectedDevice?: Device
    archiveDevice: (deviceId?: string) => Promise<void>
    restoreDevice: (deviceId?: string) => Promise<void>
}

const DeviceContext = createContext<DeviceContextValue | undefined>(undefined)

export default function ({ children }: PropsWithChildren) {
    const params = useParams()
    const { includeArchived } = useArchiveContext()
    const { deviceApi } = useApiContext()
    const { selectedOrganization } = useUserContext()
    const Toast = useToastContext()
    const deviceId = params.deviceId

    const [allDevices, allDevicesLoading, allDevicesError, refreshAllDevices] = usePaginatedTable(
        async (table: TableControl<Device>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const response = await deviceApi.getAllDevicesV2({
                sortBy: table.sortBy,
                ascending: table.ascending,
                skip: table.skip,
                take: table.take,
            })
            return mapReadTableResponse(response, table, Device)
        },
        [selectedOrganization?.id]
    )

    const [
        organizationDevices,
        organizationDevicesLoading,
        organizationDevicesError,
        refreshOrganizationDevices,
    ] = usePaginatedTable(
        async (table: TableControl<Device>) => {
            const orgId = selectedOrganization?.id
            if (!orgId) return null
            const response = await deviceApi.getAllByOrganizationUsageV2({
                tenantId: orgId,
                sortBy: table.sortBy,
                ascending: table.ascending,
                skip: table.skip,
                take: table.take,
                includeArchived,
            })
            return mapReadTableResponse(response, table, Device)
        },
        [selectedOrganization?.id, includeArchived]
    )

    const [selectedDevice, selectedLoading, selectedError, refreshSelectedDevice] =
        useImmutableObject(async () => {
            const orgId = selectedOrganization?.id
            if (!(orgId && deviceId)) return
            const response = await deviceApi.getDeviceV2({ deviceId, tenantId: orgId })
            return mapReadResponse(response, Device)
        }, [deviceId, selectedOrganization?.id])

    const [archiveLoading, archiveError, archiveDevice] = useAction(async (deviceId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && deviceId)) return
        const response = await deviceApi.archiveDeviceV2({ deviceId, tenantId: orgId })
        throwIfResponseNotOk(response)
        refreshOrganizationDevices()
        Toast.completed('archive', 'device')
    })

    const [restoreLoading, restoreError, restoreDevice] = useAction(async (deviceId?: string) => {
        const orgId = selectedOrganization?.id
        if (!(orgId && deviceId)) return
        const response = await deviceApi.restoreDeviceV2({ deviceId, tenantId: orgId })
        throwIfResponseNotOk(response)
        if (deviceId === selectedDevice?.id) refreshSelectedDevice()
        refreshOrganizationDevices()
        Toast.completed('restore', 'device')
    })

    const loading = selectedLoading || archiveLoading || restoreLoading
    const error = selectedError || archiveError || restoreError

    useEffect(() => {
        if (error) Toast.error(error, 'Device error')
    }, [error])

    const value: DeviceContextValue = {
        loading,
        error,

        // allDevices,
        organizationDevices,
        selectedDevice,
        archiveDevice,
        restoreDevice,
    }

    return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>
}

export const useDeviceContext = () => useProvideContext(DeviceContext, 'Device')
